import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import { api, ApiError, type Me } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/** Единый вход для партнёров и сотрудников Treejar: пароль или Google (по приглашению), принятие приглашения / сброс пароля
 * по ссылке из письма (?token=…). После входа staff → «/», партнёр → «/portal». */
type Mode = 'login' | 'forgot' | 'token';
type InviteInfo = { email: string; side: 'staff' | 'partner'; kind: 'invite' | 'reset'; role: string | null; partner: string | null };

const inputCls =
  'h-10 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-[13px] text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400';

export default function PortalLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { me, loading, setMe } = useAuth();
  // до входа фичефлаги недоступны, поэтому наличие Google-входа берём из открытого /auth/config
  const [googleConfigured, setGoogleConfigured] = useState(false);
  useEffect(() => {
    api
      .get<{ google_configured: boolean }>('/auth/config')
      .then((c) => setGoogleConfigured(c.google_configured))
      .catch(() => setGoogleConfigured(false));
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [name, setName] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<Mode>(params.get('token') ? 'token' : 'login');
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const token = params.get('token');

  const errorText = (code: string, extra: Record<string, string> = {}) => {
    const key = `auth.errors.${code}`;
    const txt = t(key, { ...extra, defaultValue: '' });
    return txt || t('auth.errors.generic', { code });
  };

  // уже вошли — сразу в своё приложение
  useEffect(() => {
    if (!loading && me) navigate(me.side === 'staff' ? '/' : '/portal', { replace: true });
  }, [me, loading, navigate]);

  // ошибка от Google-callback (?error=…&email=…)
  useEffect(() => {
    const err = params.get('error');
    if (err) {
      setError(errorText(err, { email: params.get('email') ?? '' }));
      const next = new URLSearchParams(params);
      next.delete('error');
      next.delete('email');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ссылка из письма: приглашение или сброс пароля
  useEffect(() => {
    if (!token) return;
    let active = true;
    api
      .get<InviteInfo>(`/auth/invite/${token}`)
      .then((info) => {
        if (!active) return;
        setInvite(info);
        setEmail(info.email);
        setMode('token');
      })
      .catch((e: ApiError) => {
        if (!active) return;
        setError(errorText(e.code));
        setMode('login');
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const afterLogin = (m: Me) => {
    setMe(m);
    const from = (location.state as { from?: string } | null)?.from;
    const target = m.side === 'staff' ? (from && !from.startsWith('/portal') ? from : '/') : from && from.startsWith('/portal') ? from : '/portal';
    navigate(target, { replace: true });
  };

  const submitLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNotice('');
    if (!email.trim() || !password.trim()) {
      setError(t('portal.login.errorEmpty'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      afterLogin(await api.post<Me>('/auth/login', { email: email.trim(), password, remember }));
    } catch (e) {
      setError(errorText((e as ApiError).code, { email: email.trim() }));
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) {
      setError(t('portal.login.errorEmpty'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await api.post('/auth/forgot', { email: email.trim() });
      setNotice(t('auth.forgotSent', { email: email.trim() }));
      setMode('login');
    } catch (e) {
      setError(errorText((e as ApiError).code));
    } finally {
      setSubmitting(false);
    }
  };

  const submitToken = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length < 8) {
      setError(t('auth.errors.password_weak'));
      return;
    }
    if (password !== password2) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      afterLogin(await api.post<Me>('/auth/accept', { token, password, full_name: name.trim() || undefined }));
    } catch (e) {
      setError(errorText((e as ApiError).code));
    } finally {
      setSubmitting(false);
    }
  };

  const roleLabel = (info: InviteInfo) => {
    if (info.side === 'partner' && info.role) return t(`portal.team.role${info.role}`);
    const map: Record<string, string> = { admin: t('set.roleAdmin'), partner_manager: t('set.rolePartner'), project_manager: t('set.roleProject'), finance: t('set.roleFinance'), viewer: t('set.roleViewer') };
    return (info.role && map[info.role]) || info.role || '';
  };

  return (
    <div className="flex min-h-screen bg-background-50 text-foreground-900">
      <section className="relative hidden w-[46%] shrink-0 flex-col justify-between bg-primary-600 px-10 py-9 text-background-50 lg:flex">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50/12 text-background-50">
              <i className="ri-plant-line text-[19px]" />
            </span>
            <div className="leading-tight">
              <p className="text-[14px] font-semibold tracking-tight">Treejar</p>
              <p className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-primary-100">{t('portal.brand')}</p>
            </div>
          </div>

          <h2 className="mt-16 max-w-[380px] text-[30px] font-semibold leading-[1.2] tracking-tight">{t('portal.login.feature1Title')}</h2>
          <p className="mt-3 max-w-[330px] text-[13px] leading-6 text-primary-100">{t('portal.login.feature1Text')}</p>

          <div className="mt-9 flex flex-col gap-5">
            {[
              { icon: 'ri-shopping-bag-3-line', title: t('portal.login.feature2Title'), text: t('portal.login.feature2Text') },
              { icon: 'ri-wallet-3-line', title: t('portal.login.feature3Title'), text: t('portal.login.feature3Text') },
            ].map((f) => (
              <div key={f.icon} className="flex gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-50/10">
                  <i className={`${f.icon} text-[14px]`} />
                </span>
                <div className="min-w-0">
                  <p className="text-[12.5px] font-semibold">{f.title}</p>
                  <p className="mt-0.5 max-w-[300px] text-[11.5px] leading-5 text-primary-100">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-background-50/15 pt-5">
          <p className="text-[10.5px] leading-4 text-primary-200">{t('portal.login.secure')}</p>
          <p className="mt-1 text-[10.5px] text-primary-200">Al Quoz Industrial 4, Dubai · United Arab Emirates</p>
        </div>
      </section>

      <section className="flex flex-1 flex-col">
        <div className="flex items-center justify-between px-5 py-4 md:px-8">
          <div className="flex items-center gap-2.5 lg:invisible">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-plant-line text-[16px]" />
            </span>
            <p className="text-[13px] font-semibold text-foreground-950">Treejar</p>
          </div>
          <LanguageSwitcher />
        </div>

        <div className="flex flex-1 items-center justify-center px-5 py-8 md:px-8">
          <div className="w-full max-w-[400px]">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-primary-700">{t('portal.supplierLine')}</p>
            <h1 className="mt-2 text-[24px] font-semibold leading-8 tracking-tight text-foreground-950">
              {mode === 'token' && invite ? (invite.kind === 'reset' ? t('auth.resetTitle') : t('auth.inviteTitle')) : mode === 'forgot' ? t('auth.forgotTitle') : t('portal.login.title')}
            </h1>
            <p className="mt-2 text-[12.5px] leading-5 text-foreground-500">
              {mode === 'token' && invite
                ? invite.kind === 'reset'
                  ? t('auth.resetBody', { email: invite.email })
                  : t('auth.inviteBody', { role: roleLabel(invite), partner: invite.partner ? t('auth.inviteBodyPartner', { partner: invite.partner }) : '', email: invite.email })
                : mode === 'forgot'
                  ? t('auth.forgotBody')
                  : t('portal.login.subtitle')}
            </p>

            {mode === 'token' && invite && (
              <form onSubmit={submitToken} className="mt-7 flex flex-col gap-3.5">
                {invite.kind === 'invite' && (
                  <label className="block">
                    <span className="mb-1.5 block text-[11.5px] font-medium text-foreground-700">{t('auth.name')}</span>
                    <div className="relative">
                      <i className="ri-user-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-foreground-400" />
                      <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" className={inputCls} />
                    </div>
                  </label>
                )}
                <label className="block">
                  <span className="mb-1.5 block text-[11.5px] font-medium text-foreground-700">{t('auth.newPassword')}</span>
                  <div className="relative">
                    <i className="ri-lock-2-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-foreground-400" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className={inputCls} />
                  </div>
                  <span className="mt-1 block text-[10.5px] text-foreground-400">{t('auth.passwordRule')}</span>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[11.5px] font-medium text-foreground-700">{t('auth.confirmPassword')}</span>
                  <div className="relative">
                    <i className="ri-lock-2-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-foreground-400" />
                    <input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} autoComplete="new-password" className={inputCls} />
                  </div>
                </label>
                {error && <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] leading-4 text-rose-700">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-1 flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 text-[13px] font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-wait disabled:opacity-80"
                >
                  {submitting ? t('portal.login.signingIn') : t('auth.activate')}
                  <i className={`${submitting ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} text-[14px]`} />
                </button>
              </form>
            )}

            {mode === 'forgot' && (
              <form onSubmit={submitForgot} className="mt-7 flex flex-col gap-3.5">
                <label className="block">
                  <span className="mb-1.5 block text-[11.5px] font-medium text-foreground-700">{t('portal.login.email')}</span>
                  <div className="relative">
                    <i className="ri-mail-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-foreground-400" />
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('portal.login.emailPlaceholder')} autoComplete="email" className={inputCls} />
                  </div>
                </label>
                {error && <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] leading-4 text-rose-700">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-1 flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 text-[13px] font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-wait disabled:opacity-80"
                >
                  {t('auth.forgotSubmit')}
                </button>
                <button type="button" onClick={() => { setMode('login'); setError(''); }} className="cursor-pointer text-[11.5px] font-semibold text-primary-700 hover:text-primary-800">
                  {t('portal.login.backToLogin')}
                </button>
              </form>
            )}

            {mode === 'login' && (
              <form onSubmit={submitLogin} className="mt-7 flex flex-col gap-3.5">
                <label className="block">
                  <span className="mb-1.5 block text-[11.5px] font-medium text-foreground-700">{t('portal.login.email')}</span>
                  <div className="relative">
                    <i className="ri-mail-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-foreground-400" />
                    <input type="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('portal.login.emailPlaceholder')} autoComplete="email" className={inputCls} />
                  </div>
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-[11.5px] font-medium text-foreground-700">{t('portal.login.password')}</span>
                  <div className="relative">
                    <i className="ri-lock-2-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-foreground-400" />
                    <input type="password" name="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('portal.login.passwordPlaceholder')} autoComplete="current-password" className={inputCls} />
                  </div>
                </label>

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="flex cursor-pointer items-center gap-2 text-[11.5px] text-foreground-600">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-3.5 w-3.5 cursor-pointer rounded border-background-400 accent-primary-600" />
                    {t('portal.login.remember')}
                  </label>
                  <button type="button" onClick={() => { setMode('forgot'); setError(''); setNotice(''); }} className="cursor-pointer whitespace-nowrap text-[11.5px] font-semibold text-primary-700 hover:text-primary-800">
                    {t('portal.login.forgot')}
                  </button>
                </div>

                {error && <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] leading-4 text-rose-700">{error}</p>}
                {notice && (
                  <div className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
                    <p className="text-[11.5px] font-semibold text-primary-800">{t('portal.login.resetTitle')}</p>
                    <p className="mt-0.5 text-[11px] leading-4 text-primary-800/80">{notice}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-1 flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 text-[13px] font-semibold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-wait disabled:opacity-80"
                >
                  {submitting ? t('portal.login.signingIn') : t('portal.login.signIn')}
                  <i className={`${submitting ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} text-[14px]`} />
                </button>

                {googleConfigured && (
                  <>
                    <div className="flex items-center gap-3 text-[10.5px] uppercase tracking-[0.12em] text-foreground-400">
                      <span className="h-px flex-1 bg-background-200" />
                      {t('auth.or')}
                      <span className="h-px flex-1 bg-background-200" />
                    </div>
                    <a
                      href="/api/auth/google"
                      className="flex h-10 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 text-[13px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <i className="ri-google-fill text-[15px]" />
                      {t('auth.google')}
                    </a>
                  </>
                )}
              </form>
            )}

            <div className="mt-5 rounded-md border border-background-200 bg-background-100 p-3">
              <p className="text-[11px] font-semibold text-foreground-800">{t('auth.accessByInvite')}</p>
              <p className="mt-0.5 text-[11px] text-foreground-500">{t('auth.staffHint')}</p>
            </div>

            <p className="mt-5 text-[10.5px] leading-4 text-foreground-400">{t('portal.login.hint')}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
